import React, { useState, useRef, useEffect } from 'react';
import { useFeatureFlags, featureFlagsUtils } from '../config/featureFlags';
import { useAppSelector } from '../store/hooks';

const DebugPanel: React.FC = () => {
  const { flags, general, auth: authConfig } = useFeatureFlags();
  const auth = useAppSelector(state => state.auth);
  const profile = useAppSelector(state => state.profile.profile);
  const [activeTab, setActiveTab] = useState<'auth' | 'profile' | 'config'>('auth');
  
  // Drag functionality
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('debugPanelPosition');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved position:', e);
      }
    }
    return { x: 20, y: window.innerHeight - 420 }; // Start at bottom left
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('debugPanelPosition', JSON.stringify(position));
  }, [position]);

  // Mouse event handlers for dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === panelRef.current || (panelRef.current?.contains(e.target as Node))) {
      setIsDragging(true);
      const rect = panelRef.current?.getBoundingClientRect();
      if (rect) {
        setDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep panel within viewport bounds
      const maxX = window.innerWidth - 350; // panel width
      const maxY = window.innerHeight - 400; // approximate panel height
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Only render if the debug panels feature flag is enabled and user is authenticated
  if (!flags.enableDebugPanels || !auth.isAuthenticated) {
    return null;
  }

  const renderAuthTab = () => (
    <div>
      <h4>Authentication State</h4>
      <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
        <div><strong>Is Authenticated:</strong> {auth.isAuthenticated ? 'Yes' : 'No'}</div>
        <div><strong>Auth Type:</strong> {auth.type || 'None'}</div>
        <div><strong>Token:</strong> {auth.token ? `${auth.token.substring(0, 20)}...` : 'None'}</div>
        <div><strong>Token Expiry:</strong> {auth.tokenExpiry ? new Date(auth.tokenExpiry).toLocaleString() : 'None'}</div>
        <div><strong>Last Activity:</strong> {new Date(auth.lastActivity).toLocaleString()}</div>
        <div><strong>Last Token Check:</strong> {new Date(auth.lastTokenCheck).toLocaleString()}</div>
        <div><strong>Error:</strong> {auth.error || 'None'}</div>
        {auth.adminUser && (
          <div><strong>Admin User:</strong> {auth.adminUser.username} ({auth.adminUser.role})</div>
        )}
      </div>
    </div>
  );

  const renderProfileTab = () => (
    <div>
      <h4>Profile State</h4>
      <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
        <div><strong>Profile Loaded:</strong> {profile ? 'Yes' : 'No'}</div>
        {profile && (
          <>
            <div><strong>User ID:</strong> {profile.id}</div>
            <div><strong>Name:</strong> {profile.firstName} {profile.lastName}</div>
            <div><strong>Email:</strong> {profile.email}</div>
            <div><strong>Account Type:</strong> {profile.accountType || 'Unknown'}</div>
          </>
        )}
      </div>
    </div>
  );

  const renderConfigTab = () => (
    <div>
      <h4>Configuration</h4>
      <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
        <div><strong>Environment:</strong> {general.environment}</div>
        <div><strong>API URL:</strong> {general.apiUrl}</div>
        <div><strong>WebSocket URL:</strong> {general.wsUrl}</div>
        <div><strong>Logging Enabled:</strong> {general.enableLogging ? 'Yes' : 'No'}</div>
        <div><strong>Session Timeout:</strong> {authConfig.sessionTimeoutMinutes} minutes</div>
        <div><strong>JWT Expiry:</strong> {authConfig.jwtExpiryMinutes} minutes</div>
        <div><strong>Refresh Threshold:</strong> {authConfig.refreshThresholdMinutes} minutes</div>
      </div>
    </div>
  );

  return (
    <div 
      ref={panelRef}
      style={{
        position: 'fixed',
        bottom: 'auto',
        left: position.x,
        top: position.y,
        width: '350px',
        maxHeight: '400px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        zIndex: 1000,
        fontSize: '14px',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
    >
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #dee2e6',
        backgroundColor: '#e9ecef',
        cursor: 'grab'
      }}>
        <div style={{
          flex: 1,
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          color: '#6c757d'
        }}>
          <span>🔧</span>
          <span>Debug Panel</span>
          <span style={{ marginLeft: 'auto' }}>(Drag to move)</span>
          <button
            onClick={() => {
              setPosition({ x: 20, y: window.innerHeight - 420 });
              localStorage.removeItem('debugPanelPosition');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#6c757d',
              cursor: 'pointer',
              fontSize: '12px',
              padding: '2px 6px',
              borderRadius: '3px',
              marginLeft: '8px'
            }}
            title="Reset position"
          >
            ↺
          </button>
        </div>
      </div>
      
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #dee2e6',
        backgroundColor: '#e9ecef'
      }}>
        <button
          onClick={() => setActiveTab('auth')}
          style={{
            flex: 1,
            padding: '8px',
            border: 'none',
            backgroundColor: activeTab === 'auth' ? '#007bff' : 'transparent',
            color: activeTab === 'auth' ? 'white' : '#495057',
            cursor: 'pointer'
          }}
        >
          Auth
        </button>
        <button
          onClick={() => setActiveTab('profile')}
          style={{
            flex: 1,
            padding: '8px',
            border: 'none',
            backgroundColor: activeTab === 'profile' ? '#007bff' : 'transparent',
            color: activeTab === 'profile' ? 'white' : '#495057',
            cursor: 'pointer'
          }}
        >
          Profile
        </button>
        <button
          onClick={() => setActiveTab('config')}
          style={{
            flex: 1,
            padding: '8px',
            border: 'none',
            backgroundColor: activeTab === 'config' ? '#007bff' : 'transparent',
            color: activeTab === 'config' ? 'white' : '#495057',
            cursor: 'pointer'
          }}
        >
          Config
        </button>
      </div>
      
      <div style={{ padding: '16px', maxHeight: '300px', overflowY: 'auto' }}>
        {activeTab === 'auth' && renderAuthTab()}
        {activeTab === 'profile' && renderProfileTab()}
        {activeTab === 'config' && renderConfigTab()}
      </div>
      
      <div style={{
        padding: '8px 16px',
        borderTop: '1px solid #dee2e6',
        backgroundColor: '#e9ecef',
        fontSize: '12px',
        color: '#6c757d'
      }}>
        🔧 Debug Panel - {featureFlagsUtils.isAdminContext() ? 'Admin' : 'Retail'} Context
      </div>
    </div>
  );
};

export default DebugPanel; 